import { useEffect } from 'react';

import { useAtomValue, useSetAtom } from 'jotai';
import { RESET } from 'jotai/utils';

import { kmcStateAtom, setKmcFallbackAtom, setKmcVerifiedAtom } from 'src/stores/kmc/atoms';
import {
  isKmcErrorMessage,
  isKmcVerifiedMessage,
  isValidKmcMessageEvent,
} from 'src/stores/kmc/utils';
import { returnLoginEntryAtom } from 'src/stores/phoneNumberLogin/atoms';
import { resetRegisterAtom } from 'src/stores/register/atoms';

const POPUP_CLOSE_POLLING_INTERVAL_MS = 250;
const POPUP_CLOSE_GRACE_WINDOW_MS = 100;

const useKmcPopupController = () => {
  const kmcState = useAtomValue(kmcStateAtom);
  const setKmcVerified = useSetAtom(setKmcVerifiedAtom);
  const setKmcFallback = useSetAtom(setKmcFallbackAtom);
  const setKmcState = useSetAtom(kmcStateAtom);
  const returnLoginEntry = useSetAtom(returnLoginEntryAtom);
  const resetRegister = useSetAtom(resetRegisterAtom);

  useEffect(() => {
    if (kmcState.status !== 'opening') return undefined;

    const { popupWindow, expectedOrigin } = kmcState;
    let closePollingId: number | null = null;
    let closeGraceTimerId: number | null = null;
    let isCleanedUp = false;
    let isPopupCloseGraceStarted = false;
    let cleanup = () => {};

    const handlePopupAbandoned = () => {
      cleanup();
      if (kmcState.entryPoint === 'sns') {
        resetRegister();
      } else {
        returnLoginEntry();
      }
      setKmcState(RESET);
    };

    const schedulePopupAbandoned = () => {
      if (isPopupCloseGraceStarted) return;

      isPopupCloseGraceStarted = true;
      closeGraceTimerId = window.setTimeout(handlePopupAbandoned, POPUP_CLOSE_GRACE_WINDOW_MS);
    };

    const checkPopupClosed = () => {
      if (popupWindow?.closed) {
        schedulePopupAbandoned();
      }
    };

    const handleMessage = (event: MessageEvent) => {
      if (!isValidKmcMessageEvent(event, expectedOrigin, popupWindow)) {
        if (event.source === popupWindow) {
          cleanup();
          setKmcFallback('invalid_message');
        }
        return;
      }

      cleanup();

      // 성공: kmcResultKey / KMC 자체 에러: kmcError → TSP fallback
      if (isKmcVerifiedMessage(event.data)) {
        setKmcVerified(event.data.kmcResultKey);
      } else if (isKmcErrorMessage(event.data)) {
        setKmcFallback('kmc_error');
      }

      popupWindow?.close();
    };

    cleanup = () => {
      if (isCleanedUp) return;

      isCleanedUp = true;
      window.removeEventListener('message', handleMessage);
      window.removeEventListener('focus', checkPopupClosed);

      if (closePollingId !== null) {
        window.clearInterval(closePollingId);
      }

      if (closeGraceTimerId !== null) {
        window.clearTimeout(closeGraceTimerId);
      }
    };

    closePollingId = window.setInterval(checkPopupClosed, POPUP_CLOSE_POLLING_INTERVAL_MS);

    window.addEventListener('message', handleMessage);
    window.addEventListener('focus', checkPopupClosed);

    return cleanup;
  }, [kmcState, resetRegister, returnLoginEntry, setKmcFallback, setKmcState, setKmcVerified]);
};

export default useKmcPopupController;
